<template>
  <!-- Figma S&R flow (Under Working – S&R Flow): breadcrumb bar, stepper, main card + Details/notes card.
       Pages keep their own logic and markup inside the default slot. -->
  <div
    class="w-full relative border-1 rounded-2xl px-[10px] laptop:px-[15px] largeDesktop:px-[20px] laptop:pt-[20px] mb-[50px] border-gray-300"
    :class="$route.name === 'last-evaluation' ? 'pb-[170px] tablet:pb-[40px] pt-[20px]' : 'pb-[40px] mobile:pt-[30px]'">
    <button v-if="showBack" type="button" aria-label="Back" :class="hideBackButton ? 'hidden' : 'flex'"
      class="laptop:hidden absolute -top-[20px] left-[10px] z-[3] w-[40px] h-[40px] p-0 rounded-full border-0 items-center justify-center bg-[#666666] shadow-[0px_0px_10px_0px_#00000026]"
      @click="$router.back()">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M19 12H5M11 6l-6 6 6 6" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"
          stroke-linejoin="round" />
      </svg>
    </button>
    <!-- breadcrumb bar: 40px white, radius 8, 13px trail. In the mobile app webview the first step ("Select a Unit")
         has no bar (only the back button); it shows from the next step on -->
    <div
      v-if="showCrumbs" class="flex flex-wrap items-center gap-x-[6px] gap-y-1 min-h-[40px] mb-[15px] px-[16px] py-[8px] rounded-[8px] bg-white shadow-[0px_0px_10px_0px_#0000001A] text-[13px] font-roboto">
      <template v-for="(crumb, index) in crumbs" :key="crumb.label">
        <span v-if="index > 0" class="text-[#CCCCCC]">|</span>
        <!-- back to that page (or replace), never a new history entry; Ctrl/Cmd/middle click still opens a tab -->
        <a v-if="crumb.to" :href="$router.resolve(crumb.to).href" class="no-underline text-[#8479E1]"
          @click="openCrumb($event, crumb.to)">{{ crumb.label }}</a>
        <span v-else :class="index === crumbs.length - 1 ? 'text-[#000000]' : 'text-[#8479E1]'">{{ crumb.label }}</span>
      </template>
    </div>

    <EvalStepper v-if="step" :step="step" class="mb-[20px]" />

    <!-- page-level validation banner (Figma C2 / D5) -->
    <slot name="alert" />

    <div class="flex mobile:flex-col laptop:flex-row gap-[20px] largeDesktop:gap-[30px] items-start">
      <!-- left column: main card, then any extra cards (e.g. Summary's Add Service / Misc. Items) -->
      <div class="flex-1 min-w-0 w-full flex flex-col gap-[20px]">
        <div
          class="flex-1 min-w-0 w-full shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] px-[10px] tablet:px-[15px] largeDesktop:px-[20px] py-[20px] bg-white">
          <div class="flex flex-wrap items-center justify-between gap-2 pb-[15px]">
            <div class="flex items-start gap-1">
              <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8] mt-[10px]" />
              <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8] mt-[10px]" />
              <div class="flex flex-col gap-1">
                <p class="text-[18px] laptop:text-[20px] font-semibold font-roboto pl-2 m-0 text-[#333333]">{{ title }}
                </p>
                <p v-if="subTitle"
                  class="m-0 mt-[-8px] mb-[20px] pl-2 text-[15px] leading-[22px] font-roboto text-[#5B616B]">
                  {{ subTitle }}
                </p>
              </div>
            </div>
            <!-- below laptop (Figma mobile) the wrapper dissolves: title-extra (e.g. Summary's edit) stays at the end of
                 the title row and the unit chip drops to its own row under it -->
            <div class="mobile:contents laptop:flex flex-wrap items-center gap-[10px]">
              <!-- Figma: unit chip on the right of the title (e.g. "New / Undiagnosed Unit") -->
              <div v-if="unitName && showUnit"
                class="mobile:order-3 mobile:basis-full laptop:order-none laptop:basis-auto">
                <div
                  class="flex justify-between items-center gap-2.5 w-fit px-3 py-2 rounded-[8px] bg-[#F7F8FB] text-[14px] font-semibold font-roboto text-[#333333]">
                  <span class="block h-[20px] w-[4px] rounded-[2px] bg-[#6C79E0]"></span>
                  {{ unitName }}
                </div>
              </div>
              <slot name="title-extra" />
            </div>
          </div>
          <slot />
          <!-- step buttons: in the card on laptop; below laptop after the Details card (Figma mobile 5908-2374) -->
          <div v-if="$slots.actions" class="mobile:hidden laptop:block">
            <slot name="actions" />
          </div>
        </div>
        <slot name="after" />
      </div>

      <!-- page-specific right card (e.g. Members' "Quote Recipient") instead of Details -->
      <div v-if="$slots.aside"
        class="w-full laptop:w-[300px] laptop:flex-shrink-0 shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] px-[15px] laptop:px-[20px] py-[20px] bg-white">
        <slot name="aside" />
      </div>
      <div v-else-if="showDetails"
        class="w-full laptop:w-[300px] desktop:w-[350px] laptop:flex-shrink-0 shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] px-[15px] laptop:px-[20px] py-[20px] bg-white">
        <div class="flex items-center gap-1 pb-[15px]">
          <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
          <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
          <p class="text-[18px] laptop:text-[20px] font-semibold font-roboto pl-2 m-0 text-[#333333]">Details</p>
        </div>
        <!-- Figma: "Appointment ID" (bookings/show-web appointment_id, G-16); booking id until the API has it -->
        <template v-if="appointmentId">
          <span
            class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Appointment
            ID:</span>
          <p
            class="mt-0 mb-[10px] mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal text-[#333333]">
            {{ appointmentId }}</p>
        </template>
        <template v-else>
          <span
            class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Booking
            ID:</span>
          <p
            class="mt-0 mb-[10px] mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal text-[#333333]">
            {{ jobId }}</p>
        </template>
        <span
          class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Consumer
          Name:</span>
        <p
          class="mt-0 mb-[20px] mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal text-[#333333]">
          {{ consumerName || '-' }}</p>
        <nest_notes v-if="bookingNotes" :is-from-job-detail="true" heading="Add Notes" />
      </div>
    </div>
    <!-- pages whose laptop buttons sit elsewhere (e.g. under Summary's extra cards) pass them as mobile-actions -->
    <div v-if="$slots['mobile-actions'] || $slots.actions" class="laptop:hidden">
      <slot name="mobile-actions">
        <slot name="actions" />
      </slot>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapActions, mapGetters } from "vuex";
import EvalStepper from "./EvalStepper.vue";
import { isNewTabClick } from "@/helpers/newTabClick";
import { goBackTo } from "@/helpers/historyPositions";
import { isSilentLogin, isFromMobile } from "@/helpers/embedMode";
import nest_notes from "./nest_notes.vue";

// trail after "Job Details" per Figma; step 1 shows only "Select a Unit"
const STEP_CRUMBS = [
  { step: 2, label: 'Equipment Diagnosis', path: 'equipment' },
  { step: 3, label: 'Summary', path: 'summary' },
  { step: 4, label: 'Finalize', path: 'final-evaluation' },
  { step: 5, label: 'Review & Submit', path: 'summary-detail' },
];

export default {
  components: { EvalStepper, nest_notes },
  props: {
    title: { type: String, required: true },
    subTitle: { type: String, required: true },
    // 1–5 for the stepper; 0 hides it
    step: { type: Number, default: 0 },
    // last breadcrumb item when it differs from the step name (e.g. "Previous Evaluation")
    crumb: { type: String, default: '' },
    // crumbs after "Job Details" for pages outside the stepper (e.g. [{ label: 'Consumer' }, { label: 'Members' }])
    trail: { type: Array, default: null },
    // replaces "Service & Repair | Job Details" for pages reached from another module (e.g. Appointments)
    baseCrumbs: { type: Array, default: null },
    jobId: { type: [String, Number], required: true },
    showDetails: { type: Boolean, default: true },
    // unit under evaluation (route query unit_id) → its name is shown as a chip
    unitId: { type: [String, Number], default: null },
    // false: no unit chip under the title (Finalize); unitId still carries into the breadcrumb links
    showUnit: { type: Boolean, default: true },
    // the round grey back button on the card's top edge (below laptop)
    showBack: { type: Boolean, default: true },
  },
  data() {
    return { consumerName: '', unitName: '', appointmentId: null };
  },
  computed: {
    ...mapGetters(["bookingNotes"]),
    // silent login / SSO users get logged out on list pages, so no link back to the S&R list
    isSilentLogin() {
      return isSilentLogin();
    },
    showCrumbs() {
      return !(isFromMobile(this.$route) && this.step === 1);
    },
    // Previous Evaluation has no grey back in Figma; the mobile app webview (no header / menu) needs it
    hideBackButton() {
      return this.$route.name === 'last-evaluation' && !isFromMobile(this.$route);
    },
    crumbs() {
      const query = { unit_id: this.unitId || undefined, is_from_mobile: this.$route.query.is_from_mobile };
      const list = [];
      if (this.baseCrumbs) return [...this.baseCrumbs, ...(this.trail || [])];
      if (!this.isSilentLogin) list.push({ label: 'Service & Repair', to: '/work-order/service-and-repair' });
      // the mobile app webview (is_from_mobile=1) has its own back navigation: no Job Details crumb there; silent login on the web keeps it
      if (!isFromMobile(this.$route)) list.push({ label: 'Job Details', to: '/job/detail/' + this.jobId });
      if (this.trail) return [...list, ...this.trail];
      if (this.step <= 1) {
        list.push({ label: this.crumb || 'Select a Unit' });
        return list;
      }
      STEP_CRUMBS.filter(c => c.step < this.step).forEach(c => {
        list.push({ label: c.label, to: { path: `/${c.path}/${this.jobId}`, query } });
      });
      const current = STEP_CRUMBS.find(c => c.step === this.step);
      list.push({ label: this.crumb || current?.label || this.title });
      return list;
    },
  },
  mounted() {
    this.loadUnitName();
    if (!this.showDetails) return;
    this.getBookingNotes({ booking_id: this.jobId });
    this.loadConsumerName();
  },
  methods: {
    ...mapActions(["getBookingNotes"]),
    openCrumb(event, to) {
      if (isNewTabClick(event)) return;
      event.preventDefault();
      goBackTo(this.$router, to);
    },
    // Direct call (not the store action) so pages watching bookingResponse are not triggered
    async loadUnitName() {
      if (!this.unitId) return;
      try {
        const res = await axios.get("units-new/list", { params: { booking_id: this.jobId } });
        const unit = (res.data?.body || []).find(u => String(u.id) === String(this.unitId));
        this.unitName = unit?.unit_name || '';
      } catch (e) {
        this.unitName = '';
      }
    },
    async loadConsumerName() {
      try {
        const res = await axios.post("bookings/show-web", { booking_id: this.jobId });
        const consumer = res.data?.body?.consumer;
        this.consumerName = typeof consumer === 'string' ? consumer : (consumer?.full_name || '');
        this.appointmentId = res.data?.body?.appointment_id || null;
      } catch (e) {
        this.consumerName = '';
      }
    },
  },
};
</script>
